import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Validation from '../../Constants/Validation';
import { resolveDeziRcn, selectIsDeziEnabled } from '../../utils';
import useDebounce from '../../hooks/useDebounce';
import {
  fetchMkonnektDiscounts,
  clearMkonnektDiscounts,
  createMkonnektDiscountRequestBody,
  resolveMkonnektMerchantId,
} from '../../redux/features/MkonnektDiscount/mkonnektDiscountSlice';

const readStoredMerchantId = () => {
  try {
    return localStorage.getItem('merchant_id');
  } catch {
    return null;
  }
};

// Effect-only component (renders nothing). Mounted once in App.
// Fetches mKonnekt (dezi) tobacco discounts on every cart change, but only for
// merchants that have dezi integration enabled. Result lives in the
// `mkonnektDiscounts` slice and is applied synchronously wherever the cart
// discount pipeline runs.
const MkonnektDiscountSync = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { filterCartData } = Validation();

  const isDeziEnabled = useSelector(selectIsDeziEnabled);
  const storeData = useSelector(
    (state) => state.singleStores?.storesData?.store_data,
  );
  const cartDataState = useSelector((state) => state.cart.cartData);
  const variantData = useSelector(
    (state) => state.variant?.variantCartData?.data,
  );
  const cartProductDetails = useSelector(
    (state) => state.cartProducts.cartProductDetails,
  );
  const authUserData = useSelector((state) => state.authUser.UserData);

  // On checkout this is the route merchant (the store being checked out), the
  // same id CouponTipSectionLogic rebuilds the request key with; elsewhere the
  // last-visited merchant. See resolveMkonnektMerchantId.
  const merchantId = resolveMkonnektMerchantId(
    location.pathname,
    readStoredMerchantId(),
  );
  // The drawer opens on pages that never load this merchant's store data (or
  // load a DIFFERENT store, e.g. brand pages), so this falls back to the rcn
  // cached the last time it WAS loaded. Sending a different rcn than the cart
  // page does would surface different discounts for the same cart.
  const rcn = resolveDeziRcn(storeData, merchantId);
  const mobileNumber = authUserData?.phone ?? '';

  // Build the mKonnekt request body from the joined cart (UPC-bearing items only).
  const requestBody = useMemo(() => {
    // No provisioned RCN means mKonnekt cannot identify this store — quoting
    // anyway just returns an empty discount set against a store it doesn't know.
    if (!isDeziEnabled || !rcn) return null;

    // variantData is only needed for products that HAVE variants; it stays
    // empty for carts of simple products (fetchVariantData only runs when a
    // variant product is added). So it must NOT be a hard precondition —
    // requiring it blocked the whole feature for non-variant tobacco carts.
    if (!cartDataState?.length || !cartProductDetails?.length) {
      return null;
    }

    const baseCart = filterCartData(
      cartDataState,
      variantData || [],
      cartProductDetails,
      { merchantId },
    );

    return createMkonnektDiscountRequestBody({
      cartItems: baseCart,
      rcn,
      mobileNumber,
      // Must match CouponTipSectionLogic's rebuild of this body exactly — the
      // serialized body is the request key checkout waits on.
      merchantId,
    });
    // filterCartData is recreated each render; intentionally excluded from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDeziEnabled,
    cartDataState,
    variantData,
    cartProductDetails,
    rcn,
    mobileNumber,
    merchantId,
  ]);

  // Debounce on a stable signature so rapid cart edits collapse into one call.
  const signature = requestBody ? JSON.stringify(requestBody) : '';
  const debouncedSignature = useDebounce(signature, 400);

  useEffect(() => {
    if (!isDeziEnabled || !debouncedSignature) {
      dispatch(clearMkonnektDiscounts());
      return;
    }
    dispatch(fetchMkonnektDiscounts(JSON.parse(debouncedSignature)));
  }, [debouncedSignature, isDeziEnabled, dispatch]);

  return null;
};

export default MkonnektDiscountSync;
